import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Settings, LogOut, User, Crown, ChevronDown, ChevronRight, Plus, X, Check, Upload, Link as LinkIcon, Calendar as CalendarIcon, Clock, Filter, Trash2, Paperclip, Bell, Repeat, Edit2, ListTodo } from 'lucide-react';
import { fetchTodos, createTodo, updateTodo, deleteTodo, toggleTodoComplete, uploadTodoFiles } from '../services/todoApi';
import Premium from './Premium';
import MemberAvatar from './MemberAvatar';
import UserAvatar from './UserAvatar';
import MemberSelector from './MemberSelector';

const TodoLists = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState('todos');
    const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [loadingFamilies, setLoadingFamilies] = useState(true);
    const [loadingTodos, setLoadingTodos] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    
    // Form states
    const [name, setName] = useState('');
    const [notes, setNotes] = useState('');
    const [assignedTo, setAssignedTo] = useState([]); // Array of member names
    const [attachments, setAttachments] = useState([]);
    const [dueDate, setDueDate] = useState('');
    const [dueTime, setDueTime] = useState('');
    const [reminderDate, setReminderDate] = useState('');
    const [reminderTime, setReminderTime] = useState('');
    const [repeat, setRepeat] = useState('none');
    const [weeklyDays, setWeeklyDays] = useState([]); // For weekly repeat
    const [monthlyDays, setMonthlyDays] = useState([]); // For monthly repeat
    const [links, setLinks] = useState([]);
    const [newLinkUrl, setNewLinkUrl] = useState('');
    const [newLinkName, setNewLinkName] = useState('');
    
    // Lists data
    const [todos, setTodos] = useState([]);
    const [shopping, setShopping] = useState([]);
    const [chores, setChores] = useState([]);
    
    // Quick add state
    const [quickAddName, setQuickAddName] = useState('');
    
    // Filter states
    const [filterStatus, setFilterStatus] = useState('pending'); // all, completed, pending (default: pending)
    
    const dropdownRef = useRef(null);
    const userMenuRef = useRef(null);

    // Set active tab from URL parameter on mount
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam && ['todos', 'shopping', 'chores'].includes(tabParam)) {
            setActiveTab(tabParam);
        }
    }, [searchParams]);

    useEffect(() => {
        const loadFamilies = async () => {
            setLoadingFamilies(true);
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                const response = await fetch(`${apiUrl}/v1/families`, {
                    credentials: 'include'
                });
                
                if (!response.ok) {
                    throw new Error('Failed to load families');
                }
                
                const families = await response.json();
                setFamilies(families);
                
                // If no families exist, redirect to famlis page
                if (families.length === 0) {
                    navigate('/famlis');
                    return;
                }
                
                // Try to restore previously selected family
                const selectedFamilyId = localStorage.getItem('selectedFamilyId');
                let familyToSelect = null;
                
                if (selectedFamilyId && families.length > 0) {
                    familyToSelect = families.find(f => f.id === selectedFamilyId);
                }
                
                // If no valid selection but there's only 1 family, auto-select it
                if (!familyToSelect && families.length === 1) {
                    familyToSelect = families[0];
                    localStorage.setItem('selectedFamilyId', families[0].id);
                }
                
                // If still no selection but there are families, auto-select the first one
                if (!familyToSelect && families.length > 0) {
                    familyToSelect = families[0];
                    localStorage.setItem('selectedFamilyId', families[0].id);
                }
                
                setSelectedFamily(familyToSelect);
            } catch (error) {
                console.error('Error loading families:', error);
            } finally {
                setLoadingFamilies(false);
            }
        };
        loadFamilies();
    }, [navigate]);

    // Load TODOs when family is selected
    useEffect(() => {
        const loadTodos = async () => {
            if (!selectedFamily) {
                setTodos([]);
                setShopping([]);
                setChores([]);
                return;
            }
            
            setLoadingTodos(true);
            try {
                // Fetch all categories in parallel
                const [todosData, shoppingData, choresData] = await Promise.all([
                    fetchTodos(selectedFamily.id, { category: 'todos' }),
                    fetchTodos(selectedFamily.id, { category: 'shopping' }),
                    fetchTodos(selectedFamily.id, { category: 'chores' })
                ]);
                
                setTodos(todosData.items || []);
                setShopping(shoppingData.items || []);
                setChores(choresData.items || []);
            } catch (error) {
                console.error('Error loading todos:', error);
                // Don't alert on initial load - just log it
            } finally {
                setLoadingTodos(false);
            }
        };
        
        loadTodos();
    }, [selectedFamily]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFamilyDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (showAddModal || showPremiumModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showAddModal, showPremiumModal]);

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };

    const handleFamilySelect = (family) => {
        setSelectedFamily(family);
        localStorage.setItem('selectedFamilyId', family.id); // ID is already a string from backend
        setShowFamilyDropdown(false);
    };

    const handleAddItem = async () => {
        if (!name.trim()) return;
        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            // Convert camelCase to snake_case for backend
            const todoData = {
                name: name.trim(),
                notes: notes.trim() || null,
                category: activeTab,
                assigned_to: assignedTo.length > 0 ? assignedTo.join(',') : null, // Comma-separated list or null
                due_date: dueDate || null,
                due_time: dueTime || null,
                reminder_date: reminderDate || null,
                reminder_time: reminderTime || null,
                repeat: repeat,
                weekly_days: repeat === 'weekly' ? weeklyDays : null,
                monthly_days: repeat === 'monthly' ? monthlyDays : null,
                links: links.length > 0 ? links : null,
                attachments: attachments.filter(att => !att.file).map(att => ({ type: att.type, name: att.name }))
            };

            let savedTodo;
            if (editingItem) {
                // Update existing TODO
                savedTodo = await updateTodo(selectedFamily.id, editingItem.id, todoData);
                
                // Update local state
                const updateList = (list) => 
                    list.map(item => item.id === editingItem.id ? savedTodo : item);
                
                if (activeTab === 'todos') setTodos(updateList(todos));
                else if (activeTab === 'shopping') setShopping(updateList(shopping));
                else if (activeTab === 'chores') setChores(updateList(chores));
            } else {
                // Create new TODO
                savedTodo = await createTodo(selectedFamily.id, todoData);
                
                // Add to local state
                if (activeTab === 'todos') setTodos([...todos, savedTodo]);
                else if (activeTab === 'shopping') setShopping([...shopping, savedTodo]);
                else if (activeTab === 'chores') setChores([...chores, savedTodo]);
            }

            // Upload new files if there are any
            const filesToUpload = attachments.filter(att => att.file).map(att => att.file);
            if (filesToUpload.length > 0 && savedTodo) {
                try {
                    const uploadResponse = await uploadTodoFiles(selectedFamily.id, savedTodo.id, filesToUpload);
                    // Update the todo with uploaded attachments
                    if (uploadResponse && uploadResponse.attachments) {
                        savedTodo.attachments = [...(savedTodo.attachments || []), ...uploadResponse.attachments];
                        // Update local state with new attachments
                        const updateList = (list) => 
                            list.map(item => item.id === savedTodo.id ? savedTodo : item);
                        if (activeTab === 'todos') setTodos(updateList(todos));
                        else if (activeTab === 'shopping') setShopping(updateList(shopping));
                        else if (activeTab === 'chores') setChores(updateList(chores));
                    }
                } catch (fileError) {
                    console.error('Error uploading files:', fileError);
                    alert('TODO saved but file upload failed: ' + fileError.message);
                }
            }

            // Reset form
            setName('');
            setNotes('');
            setAssignedTo([]);
            setAttachments([]);
            setLinks([]);
            setDueDate('');
            setDueTime('');
            setReminderDate('');
            setReminderTime('');
            setRepeat('none');
            setWeeklyDays([]);
            setMonthlyDays([]);
            setNewLinkUrl('');
            setNewLinkName('');
            setEditingItem(null);
            setShowAddModal(false);
        } catch (error) {
            console.error('Error saving todo:', error);
            alert(`Failed to save: ${error.message}`);
        }
    };

    const handleEditItem = (item) => {
        setEditingItem(item);
        setName(item.name);
        setNotes(item.notes || '');
        // Parse comma-separated assignees back to array, filtering out 'all' from old data
        const assignees = item.assigned_to ? item.assigned_to.split(',').map(name => name.trim()).filter(name => name !== 'all') : [];
        setAssignedTo(assignees);
        setAttachments(item.attachments || []);
        setLinks(item.links || []);
        setDueDate(item.due_date || '');
        setDueTime(item.due_time || '');
        setReminderDate(item.reminder_date || '');
        setReminderTime(item.reminder_time || '');
        setRepeat(item.repeat || 'none');
        setWeeklyDays(item.weekly_days || []);
        setMonthlyDays(item.monthly_days || []);
        setShowAddModal(true);
    };

    const handleCancelEdit = () => {
        setName('');
        setNotes('');
        setAssignedTo([]);
        setAttachments([]);
        setLinks([]);
        setDueDate('');
        setDueTime('');
        setReminderDate('');
        setReminderTime('');
        setRepeat('none');
        setWeeklyDays([]);
        setMonthlyDays([]);
        setNewLinkUrl('');
        setNewLinkName('');
        setEditingItem(null);
        setShowAddModal(false);
    };

    const handleToggleComplete = async (id) => {
        if (!selectedFamily) return;
        
        try {
            const updated = await toggleTodoComplete(selectedFamily.id, id);
            
            // Update local state
            const updateList = (list) =>
                list.map(item => item.id === id ? updated : item);
            
            if (activeTab === 'todos') setTodos(updateList(todos));
            else if (activeTab === 'shopping') setShopping(updateList(shopping));
            else if (activeTab === 'chores') setChores(updateList(chores));
        } catch (error) {
            console.error('Error toggling todo:', error);
            alert(`Failed to update: ${error.message}`);
        }
    };

    const handleDeleteItem = async (id) => {
        if (!selectedFamily) return;
        if (!window.confirm('Are you sure you want to delete this item?')) return;
        
        try {
            await deleteTodo(selectedFamily.id, id);
            
            // Remove from local state
            if (activeTab === 'todos') setTodos(todos.filter(item => item.id !== id));
            else if (activeTab === 'shopping') setShopping(shopping.filter(item => item.id !== id));
            else if (activeTab === 'chores') setChores(chores.filter(item => item.id !== id));
        } catch (error) {
            console.error('Error deleting todo:', error);
            alert(`Failed to delete: ${error.message}`);
        }
    };

    const handleQuickAdd = async (e) => {
        e.preventDefault();
        if (!quickAddName.trim()) return;
        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            const todoData = {
                name: quickAddName.trim(),
                category: activeTab,
                repeat: 'none'
            };

            const newTodo = await createTodo(selectedFamily.id, todoData);
            
            // Add to local state
            if (activeTab === 'todos') setTodos([...todos, newTodo]);
            else if (activeTab === 'shopping') setShopping([...shopping, newTodo]);
            else if (activeTab === 'chores') setChores([...chores, newTodo]);
            
            setQuickAddName('');
        } catch (error) {
            console.error('Error creating quick todo:', error);
            alert(`Failed to create: ${error.message}`);
        }
    };

    const handleFileAttachment = (e) => {
        const files = Array.from(e.target.files);
        const newAttachments = files.map(file => ({
            type: 'file',
            name: file.name,
            url: URL.createObjectURL(file),
            file: file  // Store the actual File object for upload
        }));
        setAttachments([...attachments, ...newAttachments]);
    };

    const handleAddLink = () => {
        if (!newLinkUrl.trim()) return;
        
        const linkToAdd = {
            url: newLinkUrl.trim(),
            name: newLinkName.trim() || newLinkUrl.trim()
        };
        
        setLinks([...links, linkToAdd]);
        setNewLinkUrl('');
        setNewLinkName('');
    };

    const removeLink = (index) => {
        setLinks(links.filter((_, i) => i !== index));
    };

    const removeAttachment = (index) => {
        setAttachments(attachments.filter((_, i) => i !== index));
    };

    const toggleWeeklyDay = (dayIndex) => {
        if (weeklyDays.includes(dayIndex)) {
            setWeeklyDays(weeklyDays.filter(d => d !== dayIndex));
        } else {
            setWeeklyDays([...weeklyDays, dayIndex].sort());
        }
    };

    const toggleMonthlyDay = (day) => {
        if (monthlyDays.includes(day)) {
            setMonthlyDays(monthlyDays.filter(d => d !== day));
        } else {
            setMonthlyDays([...monthlyDays, day].sort((a, b) => a - b));
        }
    };

    // Get family members for assignment
    const getFamilyMembers = () => {
        if (!selectedFamily || !selectedFamily.members) return [];
        return selectedFamily.members;
    };

    const getFilteredItems = () => {
        let items = [];
        if (activeTab === 'todos') items = todos;
        else if (activeTab === 'shopping') items = shopping;
        else if (activeTab === 'chores') items = chores;

        if (filterStatus === 'completed') {
            return items.filter(item => item.completed);
        } else if (filterStatus === 'pending') {
            return items.filter(item => !item.completed);
        }
        return items;
    };

    const getCurrentList = () => getFilteredItems();

    return (
        <div className="min-h-screen bg-secondary-gray">
            {/* Navbar */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => navigate('/dashboard')}
                                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                            >
                                <img src="/logo.png" alt="mifamli logo" className="h-10 w-10" />
                                <span className="text-xl font-bold text-primary-blue">mifamli</span>
                            </button>
                            
                            {/* Mi Famlis Dropdown */}
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all"
                                    style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)'
                                    }}
                                >
                                    {selectedFamily ? (
                                        <>
                                            <img 
                                                src={selectedFamily.photo} 
                                                alt={selectedFamily.name}
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span>{selectedFamily.name}</span>
                                        </>
                                    ) : (
                                        <span>Mi Famlis</span>
                                    )}
                                    <ChevronDown className={`w-4 h-4 transition-transform ${showFamilyDropdown ? 'rotate-180' : ''}`} />
                                </button>

                                {showFamilyDropdown && (
                                    <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                        {families.length > 0 ? (
                                            <>
                                                {families.map((family) => (
                                                    <button
                                                        key={family.id}
                                                        onClick={() => handleFamilySelect(family)}
                                                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 ${
                                                            selectedFamily?.id === family.id ? 'bg-blue-50' : ''
                                                        }`}
                                                    >
                                                        <img 
                                                            src={family.photo} 
                                                            alt={family.name}
                                                            className="w-10 h-10 rounded-full object-cover"
                                                        />
                                                        <div className="flex-1">
                                                            <p className="font-semibold text-gray-800">{family.name}</p>
                                                            {family.tagline && (
                                                                <p className="text-xs text-gray-500 italic truncate">"{family.tagline}"</p>
                                                            )}
                                                        </div>
                                                    </button>
                                                ))}
                                                <div className="border-t border-gray-200 my-2"></div>
                                            </>
                                        ) : (
                                            <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                                No families yet
                                            </div>
                                        )}
                                        <button
                                            onClick={() => {
                                                setShowFamilyDropdown(false);
                                                navigate('/famlis');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-primary-blue/5 flex items-center gap-3 text-primary-blue font-semibold"
                                        >
                                            <Plus className="w-5 h-5" />
                                            <span>Add Family</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            {/* Premium Button */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowPremiumModal(true)}
                                    className="relative px-4 py-2 rounded-lg font-semibold text-white hover:scale-105 transform transition-all duration-200 backdrop-blur-md border-2 border-white/50 overflow-hidden group"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(236, 72, 153, 0.85) 100%)',
                                        boxShadow: '0 0 20px rgba(59, 130, 246, 0.6), 0 0 40px rgba(236, 72, 153, 0.6)'
                                    }}
                                >
                                    <span className="relative z-10 flex items-center gap-2">
                                        <span className="hidden sm:inline">Go Premium</span>
                                        <span className="sm:hidden">Premium</span>
                                    </span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/50 to-pink-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                                </button>
                                
                                {/* Golden Crown Overlay */}
                                <div className="absolute -top-1 -right-1 transform rotate-12">
                                    <Crown className="w-5 h-5 text-yellow-500 fill-yellow-400 drop-shadow-lg" />
                                </div>
                            </div>
                            
                            {/* User Avatar Menu */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                                >
                                    <UserAvatar size="medium" />
                                </button>

                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                        <div className="px-4 py-3 border-b border-gray-200">
                                            <p className="text-sm font-semibold text-gray-800">User Account</p>
                                            <p className="text-xs text-gray-500">Manage your profile</p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                alert('Profile settings coming soon!');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <User className="w-5 h-5 text-gray-600" />
                                            <span>Profile</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                alert('Settings coming soon!');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <Settings className="w-5 h-5 text-gray-600" />
                                            <span>Settings</span>
                                        </button>
                                        <div className="border-t border-gray-200 my-2"></div>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                handleLogout();
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-red-600 font-semibold"
                                        >
                                            <LogOut className="w-5 h-5" />
                                            <span>Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <div className="mb-4 flex items-center gap-2 text-sm">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-primary-blue transition-colors"
                    >
                        Dashboard
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">Lists</span>
                </div>

                {/* Header */}
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Lists</h1>
                        <p className="text-gray-600 mt-1">Manage your to-dos, shopping, and chores</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => setShowFilterModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            <Filter className="w-4 h-4" />
                            Filter
                        </button>
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <Plus className="w-5 h-5" />
                            Add Item
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-md mb-6">
                    <div className="flex border-b border-gray-200">
                        <button
                            onClick={() => {
                                setActiveTab('todos');
                                setSearchParams({ tab: 'todos' });
                            }}
                            className={`flex-1 px-6 py-4 font-semibold transition-colors ${
                                activeTab === 'todos'
                                    ? 'text-primary-blue border-b-2 border-primary-blue'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            To Dos ({todos.length})
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab('shopping');
                                setSearchParams({ tab: 'shopping' });
                            }}
                            className={`flex-1 px-6 py-4 font-semibold transition-colors ${
                                activeTab === 'shopping'
                                    ? 'text-primary-blue border-b-2 border-primary-blue'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            Shopping ({shopping.length})
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab('chores');
                                setSearchParams({ tab: 'chores' });
                            }}
                            className={`flex-1 px-6 py-4 font-semibold transition-colors ${
                                activeTab === 'chores'
                                    ? 'text-primary-blue border-b-2 border-primary-blue'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            Chores ({chores.length})
                        </button>
                    </div>
                </div>

                {/* List Items */}
                <div className="space-y-4">
                    {/* Loading State - Show while families are loading */}
                    {loadingFamilies && (
                        <div className="bg-white rounded-xl shadow-md p-12 text-center">
                            <div className="flex items-center justify-center mb-4">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue"></div>
                            </div>
                            <p className="text-gray-600 text-lg">Loading...</p>
                        </div>
                    )}
                    
                    {/* Content - Only show after families are loaded and family is selected */}
                    {!loadingFamilies && selectedFamily && (
                        <>
                            {/* Quick Add Input */}
                            <form onSubmit={handleQuickAdd} className="bg-white rounded-xl shadow-md p-6 border-2 border-dashed border-gray-300 hover:border-primary-blue transition-colors">
                            <div className="flex items-center gap-4">
                                <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center flex-shrink-0">
                                    <Plus className="w-4 h-4 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    value={quickAddName}
                                    onChange={(e) => setQuickAddName(e.target.value)}
                                    placeholder="Add a new item..."
                                    className="flex-1 text-lg font-semibold text-gray-900 placeholder-gray-400 focus:outline-none bg-transparent"
                                />
                                {quickAddName && (
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                                    >
                                        Add
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(true)}
                                    className="p-2 text-gray-400 hover:text-primary-blue hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Add with more details"
                                >
                                    <Settings className="w-5 h-5" />
                                </button>
                            </div>
                            </form>
                            
                            {/* Loading Todos State */}
                            {loadingTodos && (
                                <div className="bg-white rounded-xl shadow-md p-12 text-center">
                                    <div className="flex items-center justify-center mb-4">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-blue"></div>
                                    </div>
                                    <p className="text-gray-600">Loading items...</p>
                                </div>
                            )}
                            
                            {/* TODO List - Show when not loading */}
                            {!loadingTodos && getCurrentList().length > 0 && getCurrentList().map((item) => (
                        <div key={item.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                            <div className="flex items-start gap-4">
                                <button
                                    onClick={() => handleToggleComplete(item.id)}
                                    className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                                        item.completed
                                            ? 'bg-primary-green border-primary-green'
                                            : 'border-gray-300 hover:border-primary-green'
                                    }`}
                                >
                                    {item.completed && <Check className="w-4 h-4 text-white" />}
                                </button>
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-1">
                                        <h3 className={`text-lg font-semibold ${item.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                            {item.name}
                                        </h3>
                                        {/* Assigned To - Show prominently if assigned */}
                                        {item.assigned_to && item.assigned_to.split(',').map((assignee, index) => {
                                            const trimmedAssignee = assignee.trim();
                                            // Don't show 'all' - it's from old data
                                            if (trimmedAssignee === 'all' || !trimmedAssignee) return null;
                                            return (
                                                <div key={index} className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-full">
                                                    <User className="w-4 h-4 text-blue-600" />
                                                    <span className="text-sm font-medium text-blue-700">{trimmedAssignee}</span>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Meta Information */}
                                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                                        {/* Due Date */}
                                        {item.due_date && (
                                            <div className="flex items-center gap-1 bg-orange-50 px-2 py-1 rounded text-orange-700">
                                                <CalendarIcon className="w-4 h-4" />
                                                <span className="font-medium">{new Date(item.due_date).toLocaleDateString()}</span>
                                                {item.due_time && <span>at {item.due_time}</span>}
                                            </div>
                                        )}

                                        {/* Completed By */}
                                        {item.completed && item.completed_by && (
                                            <div className="flex items-center gap-2 text-green-600">
                                                <span className="text-xs">Completed:</span>
                                                <MemberAvatar name={item.completed_by} size="xs" />
                                            </div>
                                        )}

                                        {/* Reminder Date */}
                                        {item.reminder_date && (
                                            <div className="flex items-center gap-1">
                                                <Bell className="w-4 h-4" />
                                                <span>{new Date(item.reminder_date).toLocaleDateString()}</span>
                                                {item.reminder_time && <span>at {item.reminder_time}</span>}
                                            </div>
                                        )}

                                        {/* Repeat */}
                                        {item.repeat && item.repeat !== 'none' && (
                                            <div className="flex items-center gap-1">
                                                <Repeat className="w-4 h-4" />
                                                <span className="capitalize">{item.repeat}</span>
                                            </div>
                                        )}

                                        {/* Attachments count indicator */}
                                        {item.attachments && item.attachments.length > 0 && (
                                            <div className="flex items-center gap-1">
                                                <Paperclip className="w-4 h-4" />
                                                <span>{item.attachments.length}</span>
                                            </div>
                                        )}

                                        {/* Links count indicator */}
                                        {item.links && item.links.length > 0 && (
                                            <div className="flex items-center gap-1">
                                                <LinkIcon className="w-4 h-4" />
                                                <span>{item.links.length}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleEditItem(item)}
                                        className="p-2 text-gray-400 hover:text-primary-blue hover:bg-blue-50 rounded-lg transition-colors"
                                        title="Edit item"
                                    >
                                        <Edit2 className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteItem(item.id)}
                                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Delete item"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                    
                    {/* Empty State - Show when todos are loaded but empty */}
                    {!loadingTodos && getCurrentList().length === 0 && (
                        <div className="bg-white rounded-xl shadow-md p-12 text-center">
                            <div className="mb-4">
                                <ListTodo className="w-16 h-16 text-gray-400 mx-auto" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                No {activeTab === 'todos' ? 'To Dos' : activeTab === 'shopping' ? 'Shopping Items' : 'Chores'} Yet
                            </h3>
                            <p className="text-gray-600 mb-4">
                                {activeTab === 'todos' 
                                    ? 'Start by adding your first to-do item above!'
                                    : activeTab === 'shopping'
                                    ? 'Start by adding your first shopping item above!'
                                    : 'Start by adding your first chore above!'}
                            </p>
                        </div>
                    )}
                        </>
                    )}
                </div>
            </div>

            {/* Add Item Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-900">
                                {editingItem ? 'Edit' : 'Add'} {activeTab === 'todos' ? 'To Do' : activeTab === 'shopping' ? 'Shopping Item' : 'Chore'}
                            </h2>
                            <button
                                onClick={handleCancelEdit}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-5">
                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Enter item name"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                />
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Add notes"
                                    rows="3"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent resize-none"
                                />
                            </div>

                            {/* Assign To */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Assign To {assignedTo.length > 0 && `(${assignedTo.length} selected)`}
                                </label>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {getFamilyMembers().map((member, index) => {
                                        const isSelected = assignedTo.includes(member.name);
                                        return (
                                            <label key={index} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => {
                                                        if (isSelected) {
                                                            setAssignedTo(assignedTo.filter(name => name !== member.name));
                                                        } else {
                                                            setAssignedTo([...assignedTo, member.name]);
                                                        }
                                                    }}
                                                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <MemberAvatar name={member.name} photoUrl={member.photo} size="sm" />
                                                <span className="text-gray-900">{member.name}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Set Date & Reminder */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Set Date & Reminder</label>
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Due Date</label>
                                            <input
                                                type="date"
                                                value={dueDate}
                                                onChange={(e) => setDueDate(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Due Time</label>
                                            <input
                                                type="time"
                                                value={dueTime}
                                                onChange={(e) => setDueTime(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Reminder Date</label>
                                            <input
                                                type="date"
                                                value={reminderDate}
                                                onChange={(e) => setReminderDate(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Reminder Time</label>
                                            <input
                                                type="time"
                                                value={reminderTime}
                                                onChange={(e) => setReminderTime(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Repeat */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Repeat</label>
                                <select
                                    value={repeat}
                                    onChange={(e) => {
                                        setRepeat(e.target.value);
                                        if (e.target.value !== 'weekly') setWeeklyDays([]);
                                        if (e.target.value !== 'monthly') setMonthlyDays([]);
                                    }}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent mb-3"
                                >
                                    <option value="none">No Repeat</option>
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="yearly">Yearly</option>
                                </select>

                                {repeat === 'weekly' && (
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-2">Select Weekdays</label>
                                        <div className="flex flex-wrap gap-2">
                                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                                                <button
                                                    key={day}
                                                    type="button"
                                                    onClick={() => toggleWeeklyDay(index)}
                                                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                                        weeklyDays.includes(index)
                                                            ? 'bg-primary-blue text-white'
                                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    {day}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {repeat === 'monthly' && (
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-2">Select Days of Month</label>
                                        <div className="grid grid-cols-7 gap-2">
                                            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                                <button
                                                    key={day}
                                                    type="button"
                                                    onClick={() => toggleMonthlyDay(day)}
                                                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                                        monthlyDays.includes(day)
                                                            ? 'bg-primary-blue text-white'
                                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    {day}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Category */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                                <input
                                    type="text"
                                    value={activeTab === 'todos' ? 'To-Dos' : activeTab === 'shopping' ? 'Shopping' : 'Chores'}
                                    disabled
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                                />
                            </div>

                            {/* Upload File */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Upload Files</label>
                                <label className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors border-2 border-dashed border-gray-300">
                                    <Upload className="w-5 h-5 text-gray-600" />
                                    <span className="text-sm font-medium text-gray-700">Choose files to upload</span>
                                    <input
                                        type="file"
                                        multiple
                                        onChange={handleFileAttachment}
                                        className="hidden"
                                    />
                                </label>
                                {attachments.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                        {attachments.map((attachment, index) => (
                                            <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                                                <div className="flex items-center gap-2">
                                                    <Paperclip className="w-4 h-4 text-gray-600" />
                                                    <span className="text-sm text-gray-700">{attachment.name}</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeAttachment(index)}
                                                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                                                >
                                                    <X className="w-4 h-4 text-gray-600" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Add Links */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Add Links</label>
                                <div className="space-y-3">
                                    <div className="flex gap-2">
                                        <input
                                            type="url"
                                            value={newLinkUrl}
                                            onChange={(e) => setNewLinkUrl(e.target.value)}
                                            placeholder="Enter URL (e.g., https://example.com)"
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={newLinkName}
                                            onChange={(e) => setNewLinkName(e.target.value)}
                                            placeholder="Display name (optional)"
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddLink}
                                            disabled={!newLinkUrl.trim()}
                                            className="px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Add
                                        </button>
                                    </div>
                                </div>
                                
                                {links.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                        {links.map((link, index) => (
                                            <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                    <LinkIcon className="w-4 h-4 text-gray-600 flex-shrink-0" />
                                                    <a
                                                        href={link.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-sm text-blue-600 hover:underline truncate"
                                                        title={link.url}
                                                    >
                                                        {link.name}
                                                    </a>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeLink(index)}
                                                    className="p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                                                >
                                                    <X className="w-4 h-4 text-gray-600" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={handleCancelEdit}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddItem}
                                disabled={!name}
                                className="px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {editingItem ? 'Update Item' : 'Add Item'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Filter Modal */}
            {showFilterModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full">
                        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-900">Filter Items</h2>
                            <button
                                onClick={() => setShowFilterModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-3">
                            <button
                                onClick={() => {
                                    setFilterStatus('all');
                                    setShowFilterModal(false);
                                }}
                                className={`w-full px-4 py-3 text-left rounded-lg transition-colors ${
                                    filterStatus === 'all'
                                        ? 'bg-primary-blue text-white'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                }`}
                            >
                                All Items
                            </button>
                            <button
                                onClick={() => {
                                    setFilterStatus('pending');
                                    setShowFilterModal(false);
                                }}
                                className={`w-full px-4 py-3 text-left rounded-lg transition-colors ${
                                    filterStatus === 'pending'
                                        ? 'bg-primary-blue text-white'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                }`}
                            >
                                Pending
                            </button>
                            <button
                                onClick={() => {
                                    setFilterStatus('completed');
                                    setShowFilterModal(false);
                                }}
                                className={`w-full px-4 py-3 text-left rounded-lg transition-colors ${
                                    filterStatus === 'completed'
                                        ? 'bg-primary-blue text-white'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                }`}
                            >
                                Completed
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>
                        
                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default TodoLists;

